import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Palette } from "lucide-react";

const HERO_IMAGE = "/hero.jpg";

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden"
    >
      {/* Soft decorative blob */}
      <div className="absolute -top-24 -right-16 w-[420px] h-[420px] rounded-full bg-terracotta/15 blur-3xl pointer-events-none" />
      <div className="absolute top-40 -left-20 w-[360px] h-[360px] rounded-full bg-sage/25 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: "easeOut" }}
          className="lg:col-span-7"
        >
          <span
            data-testid="hero-overline"
            className="inline-block text-xs md:text-sm uppercase tracking-[0.28em] text-terracotta mb-6"
          >
            Begleitetes Malen · Freie Kunst · Kassel
          </span>
          <h1
            data-testid="hero-headline"
            className="font-serif-elegant text-5xl sm:text-6xl md:text-7xl leading-[1.05] tracking-tight text-ocean"
          >
            Raum für <em className="not-italic text-terracotta">Entfaltung</em>:
            <br />
            Begleitetes Malen &amp; Freie Kunst
          </h1>
          <p
            data-testid="hero-subheadline"
            className="mt-8 text-lg md:text-xl text-ink/75 max-w-2xl leading-relaxed font-light"
          >
            Entdecke die heilende Kraft der Kreativität und finde neue Wege zu
            dir selbst, durch achtsame Begleitung oder inspirierende
            Kunstwerke.
          </p>

          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#angebote"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-terracotta text-cream text-sm tracking-wide hover:bg-terracotta-hover transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl"
            >
              Mehr erfahren
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
            <a
              href="#kunst"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-ocean/25 text-ocean text-sm tracking-wide hover:bg-ocean hover:text-cream transition-all duration-300"
            >
              <Palette size={16} />
              Meine Kunst entdecken
            </a>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
            {[
              { n: "15+", l: "Jahre Erfahrung" },
              { n: "1:1", l: "Individuelle Begleitung" },
              { n: "∞", l: "Ausdrucksmöglichkeiten" },
            ].map((s) => (
              <div key={s.l} className="border-l-2 border-sage/60 pl-4">
                <div className="font-serif-elegant text-3xl text-ocean">{s.n}</div>
                <div className="text-xs text-ink/60 uppercase tracking-widest mt-1">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.15 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative aspect-[4/5] rounded-[2rem] overflow-hidden shadow-2xl shadow-ink/10">
            <img
              src={HERO_IMAGE}
              alt="Kunstwerk von Kerstin Cloodt · Sonnenuntergang am See, Original aus dem Atelier in Kassel"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-ocean/25 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-8 -left-8 hidden md:block bg-cream border border-ink/10 rounded-2xl shadow-xl p-5 max-w-[240px]">
            <p className="font-serif-elegant italic text-ocean text-lg leading-snug">
              „Jeder Pinselstrich ist ein Schritt zu dir selbst.&ldquo;
            </p>
            <p className="mt-2 text-xs uppercase tracking-widest text-terracotta">
              — Kerstin
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
